import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  BookOpen, ClipboardList, ArrowLeft, GraduationCap, Play, CheckCircle,
  ExternalLink, FileText, Link as LinkIcon, Volume2, Video, ChevronRight, Clock,
} from 'lucide-react';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import PaystackCheckout from '../../components/PaystackCheckout';

const PROG_COLORS = [
  { from: '#1B2A5E', to: '#2D3F7A' },
  { from: '#1B5E20', to: '#2E7D32' },
  { from: '#4A148C', to: '#6A1B9A' },
  { from: '#C9A84C', to: '#A07C28' },
  { from: '#880E4F', to: '#C2185B' },
  { from: '#006064', to: '#00838F' },
];

const ADMISSION_WINDOWS = [
  { value: 'JAN', label: 'January 2026 Intake' },
  { value: 'APR', label: 'April 2026 Intake' },
  { value: 'JUL', label: 'July 2026 Intake' },
  { value: 'OCT', label: 'October 2026 Intake' },
];

function getProgress(userId, programmeId) {
  try { return JSON.parse(localStorage.getItem(`hmu_prog_${userId}_${programmeId}`) || '{}'); }
  catch { return {}; }
}
function saveProgress(userId, programmeId, lessonId, data) {
  const cur = getProgress(userId, programmeId);
  localStorage.setItem(`hmu_prog_${userId}_${programmeId}`, JSON.stringify({ ...cur, [lessonId]: { ...cur[lessonId], ...data } }));
}

function detectType(url = '') {
  if (!url) return 'none';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (/\.(mp4|webm|ogg|mov)$/i.test(url)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/i.test(url)) return 'audio';
  if (/\.pdf$/i.test(url) || url.includes('/pdf')) return 'pdf';
  return 'link';
}

function LessonTypeIcon({ url, type }) {
  const ct = detectType(url) || type;
  if (ct === 'youtube' || ct === 'video') return <Video className="w-4 h-4 text-[#1B2A5E]" />;
  if (ct === 'audio') return <Volume2 className="w-4 h-4 text-purple-600" />;
  if (ct === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
  if (ct === 'link') return <LinkIcon className="w-4 h-4 text-slate-400" />;
  return <BookOpen className="w-4 h-4 text-[#1B2A5E]" />;
}

function LessonViewer({ lesson, userId, programmeId, onClose, onComplete }) {
  const videoRef = useRef(null);
  const [completed, setCompleted] = useState(() => !!getProgress(userId, programmeId)[lesson.lesson_id]?.completed);

  const markComplete = () => {
    saveProgress(userId, programmeId, lesson.lesson_id, { completed: true, completedAt: new Date().toISOString() });
    setCompleted(true);
    if (onComplete) onComplete(lesson.lesson_id);
  };

  const ct = detectType(lesson.content_url);
  const ytEmbed = lesson.content_url
    ? lesson.content_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
    : '';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <DialogHeader><DialogTitle className="text-left text-[#1B2A5E] pr-8">{lesson.title}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className="text-[10px]">{lesson.lesson_type}</Badge>
              {lesson.teacher_name && <span className="text-xs text-slate-400">By {lesson.teacher_name}</span>}
              {completed && <Badge className="bg-green-100 text-green-700 text-[10px] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</Badge>}
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {ct === 'youtube' && (
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              <iframe src={ytEmbed} className="w-full h-full" allowFullScreen title={lesson.title} />
            </div>
          )}
          {ct === 'video' && (
            <video ref={videoRef} src={lesson.content_url} controls className="w-full rounded-xl" onEnded={markComplete}>
              Your browser does not support the video tag.
            </video>
          )}
          {ct === 'audio' && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
              <audio src={lesson.content_url} controls className="w-full" onEnded={markComplete} />
            </div>
          )}
          {ct === 'pdf' && (
            <div className="space-y-2">
              <iframe src={lesson.content_url} className="w-full h-[500px] rounded-xl border border-slate-200" title={lesson.title} />
              <a href={lesson.content_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#1B2A5E] hover:underline">
                <ExternalLink className="w-4 h-4" /> Open in new tab
              </a>
            </div>
          )}
          {ct === 'link' && lesson.content_url && (
            <a href={lesson.content_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl border border-[#C8D0E8] bg-[#EEF0F8] hover:bg-[#C8D0E8] transition-colors">
              <ExternalLink className="w-5 h-5 text-[#1B2A5E]" />
              <span className="text-sm text-[#1B2A5E] font-medium">Open Resource</span>
            </a>
          )}
          {lesson.description && <div className="text-sm text-slate-600 bg-slate-50 rounded-xl p-4">{lesson.description}</div>}
          {lesson.content_text && (
            <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white border border-slate-100 rounded-xl p-4 max-h-64 overflow-y-auto">
              {lesson.content_text}
            </div>
          )}
          {(ct === 'youtube' || ct === 'link' || ct === 'none') && !completed && (
            <Button onClick={markComplete} className="w-full rounded-xl bg-[#1B2A5E] hover:bg-[#152248] text-white">
              <CheckCircle className="w-4 h-4 mr-2" /> Mark as Complete
            </Button>
          )}
          {completed && (
            <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-medium py-2">
              <CheckCircle className="w-4 h-4" /> Lesson completed
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StudentProgrammes() {
  const { user } = useAuth();
  const [enrolledProgrammes, setEnrolledProgrammes] = useState([]);
  const [allProgrammes, setAllProgrammes] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProg, setSelectedProg] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('lessons');
  const [progProgress, setProgProgress] = useState({});

  // Enrolment flow
  const [enrolProg, setEnrolProg] = useState(null);
  const [enrolWindow, setEnrolWindow] = useState('JAN');
  const [enrolDialogOpen, setEnrolDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [enrolRes, progRes, lesRes, asgnRes, quizRes] = await Promise.all([
        api.get('/enrolments/my').catch(() => ({ data: [] })),
        api.get('/academic/programmes/public').catch(() => ({ data: [] })),
        api.get('/lms/lessons', { params: { published: true } }).catch(() => ({ data: [] })),
        api.get('/assignments').catch(() => ({ data: [] })),
        api.get('/quiz/student/available').catch(() => ({ data: [] })),
      ]);
      setEnrolledProgrammes(enrolRes.data || []);
      setAllProgrammes(progRes.data || []);
      setLessons(lesRes.data || []);
      setAssignments(asgnRes.data || []);
      setQuizzes(quizRes.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openProgramme = (ep) => {
    setSelectedProg(ep);
    setActiveTab('lessons');
    setProgProgress(getProgress(user.user_id, ep.programme_id));
  };

  const openEnrolDialog = (prog) => {
    setEnrolProg(prog);
    setEnrolWindow('JAN');
    setEnrolDialogOpen(true);
  };

  const handleEnrolSuccess = () => {
    setEnrolDialogOpen(false);
    fetchData();
  };

  const handleLessonComplete = (lessonId) => {
    setProgProgress(prev => ({ ...prev, [lessonId]: { ...prev[lessonId], completed: true } }));
  };

  if (loading) return <HomeUniLoader />;

  const enrolledIds = new Set(enrolledProgrammes.map(e => e.programme_id));
  const availableProgrammes = allProgrammes.filter(p => !enrolledIds.has(p.programme_id));

  // ── Programme Detail View ────────────────────────────────────────────────────
  if (selectedProg) {
    const progId = selectedProg.programme_id;
    const progName = selectedProg.programme_name;
    const colorIdx = enrolledProgrammes.findIndex(e => e.programme_id === progId) % PROG_COLORS.length;
    const colors = PROG_COLORS[colorIdx < 0 ? 0 : colorIdx];

    const progLessons = lessons.filter(l => l.programme_id === progId || l.programme_name === progName);
    const progAssignments = assignments.filter(a => a.programme_id === progId || a.programme_name === progName);
    const progQuizzes = quizzes.filter(q => q.programme_id === progId || q.programme_name === progName);
    const progResources = progLessons.filter(l => l.lesson_type === 'link' || (l.content_url && detectType(l.content_url) === 'link'));

    const completedCount = progLessons.filter(l => progProgress[l.lesson_id]?.completed).length;
    const totalLessons = progLessons.length;
    const pct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

    return (
      <div className="space-y-5" data-testid="programme-detail">
        {selectedLesson && (
          <LessonViewer lesson={selectedLesson} userId={user.user_id} programmeId={progId}
            onClose={() => setSelectedLesson(null)} onComplete={handleLessonComplete} />
        )}

        <Button variant="ghost" size="sm" onClick={() => setSelectedProg(null)} className="text-[#1B2A5E]">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to My Programmes
        </Button>

        <div className="rounded-xl overflow-hidden border border-[#C8D0E8] shadow-sm">
          <div className="h-28 flex items-end px-6 pb-4" style={{ background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)` }}>
            <div className="flex items-end gap-4 w-full">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 pb-0.5">
                <h2 className="text-xl font-bold text-white">{progName}</h2>
                <p className="text-white/70 text-sm">{selectedProg.cohort || `${selectedProg.admission_window} Cohort`}</p>
              </div>
              <div className="text-right pb-0.5">
                <p className="text-white/90 font-bold text-lg">{pct}%</p>
                <p className="text-white/60 text-xs">{completedCount}/{totalLessons} complete</p>
              </div>
            </div>
          </div>
          <div className="bg-white">
            <div className="h-1.5 bg-slate-100">
              <div className="h-full bg-gradient-to-r from-[#1B2A5E] to-[#C9A84C] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="px-5 py-3 flex items-center gap-4 flex-wrap">
              <Badge className={selectedProg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                {selectedProg.status || 'active'}
              </Badge>
              <span className="text-xs text-slate-500">{progLessons.length} lessons</span>
              <span className="text-xs text-slate-500">{progAssignments.length} assignments</span>
              <span className="text-xs text-slate-500">{progQuizzes.length} quizzes</span>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start">
            <TabsTrigger value="lessons" className="text-xs">Lessons ({progLessons.length})</TabsTrigger>
            <TabsTrigger value="assignments" className="text-xs">Assignments ({progAssignments.length})</TabsTrigger>
            <TabsTrigger value="quizzes" className="text-xs">Quizzes ({progQuizzes.length})</TabsTrigger>
            <TabsTrigger value="resources" className="text-xs">Resources ({progResources.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="lessons">
            <Card className="rounded-xl border-slate-100"><CardContent className="pt-4">
              {progLessons.length > 0 ? (
                <div className="space-y-2">
                  {progLessons.map(l => {
                    const done = progProgress[l.lesson_id]?.completed;
                    return (
                      <div key={l.lesson_id} onClick={() => setSelectedLesson(l)}
                        className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:border-[#1B2A5E] hover:bg-[#EEF0F8] cursor-pointer transition-all group">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-100' : 'bg-[#EEF0F8]'}`}>
                          {done ? <CheckCircle className="w-4 h-4 text-green-600" /> : <LessonTypeIcon url={l.content_url} type={l.lesson_type} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1B2A5E] truncate">{l.title}</p>
                          <p className="text-[10px] text-slate-400">{l.lesson_type}{l.teacher_name ? ` · ${l.teacher_name}` : ''}{l.duration_minutes ? ` · ${l.duration_minutes} min` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {done && <Badge className="bg-green-100 text-green-700 text-[10px]">Done</Badge>}
                          <Play className="w-4 h-4 text-slate-300 group-hover:text-[#1B2A5E]" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400"><BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No lessons published yet</p></div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="assignments">
            <Card className="rounded-xl border-slate-100"><CardContent className="pt-4">
              {progAssignments.length > 0 ? (
                <div className="space-y-2">
                  {progAssignments.map(a => {
                    const submitted = !!a.my_submission;
                    return (
                      <div key={a.assignment_id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${submitted ? 'bg-[#EEF0F8]' : 'bg-amber-100'}`}>
                          <ClipboardList className={`w-4 h-4 ${submitted ? 'text-[#1B2A5E]' : 'text-amber-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1B2A5E] truncate">{a.title}</p>
                          <p className="text-[10px] text-slate-400">
                            {a.due_date ? `Due: ${new Date(a.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ` : ''}
                            Max: {a.max_score} pts{a.teacher_name ? ` · ${a.teacher_name}` : ''}
                          </p>
                        </div>
                        {submitted
                          ? <Badge className="bg-[#EEF0F8] text-[#1B2A5E] text-[10px] flex-shrink-0">Submitted</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 text-[10px] flex-shrink-0">Pending</Badge>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400"><ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No assignments yet</p></div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="quizzes">
            <Card className="rounded-xl border-slate-100"><CardContent className="pt-4">
              {progQuizzes.length > 0 ? (
                <div className="space-y-2">
                  {progQuizzes.map(q => {
                    const submitted = !!q.my_submission;
                    return (
                      <div key={q.quiz_id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:border-[#1B2A5E] cursor-pointer transition-all">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${submitted ? 'bg-green-100' : 'bg-indigo-50'}`}>
                          {submitted ? <CheckCircle className="w-4 h-4 text-green-600" /> : <FileText className="w-4 h-4 text-indigo-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1B2A5E] truncate">{q.title}</p>
                          <p className="text-[10px] text-slate-400">{q.quiz_type?.toUpperCase()} · {q.question_count || q.questions?.length || 0} questions</p>
                        </div>
                        {submitted
                          ? <div className="text-right flex-shrink-0"><p className="text-xs font-bold text-green-600">{q.my_submission?.percentage?.toFixed(0)}%</p></div>
                          : <Badge className="bg-indigo-100 text-indigo-700 text-[10px] flex-shrink-0">Attempt</Badge>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400"><FileText className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No quizzes yet</p></div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="resources">
            <Card className="rounded-xl border-slate-100"><CardContent className="pt-4">
              {progResources.length > 0 ? (
                <div className="space-y-2">
                  {progResources.map(l => (
                    <a key={l.lesson_id} href={l.content_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:border-[#1B2A5E] hover:bg-[#EEF0F8] transition-all">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1B2A5E] truncate">{l.title}</p>
                        <p className="text-[10px] text-slate-400 truncate">{l.content_url}</p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400"><ExternalLink className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No resources linked yet</p></div>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ── Programme Grid ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="student-programmes">
      <div>
        <h1 className="text-2xl font-bold text-[#1B2A5E]">My Programmes</h1>
        <p className="text-slate-500 mt-1">{enrolledProgrammes.length} programme{enrolledProgrammes.length !== 1 ? 's' : ''} enrolled</p>
      </div>

      {enrolledProgrammes.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {enrolledProgrammes.map((ep, i) => {
            const colors = PROG_COLORS[i % PROG_COLORS.length];
            const progress = getProgress(user.user_id, ep.programme_id);
            const progLessons = lessons.filter(l => l.programme_id === ep.programme_id || l.programme_name === ep.programme_name);
            const completedCount = progLessons.filter(l => progress[l.lesson_id]?.completed).length;
            const pct = progLessons.length > 0 ? Math.round((completedCount / progLessons.length) * 100) : 0;
            return (
              <div key={ep.programme_id} onClick={() => openProgramme(ep)}
                className="rounded-xl overflow-hidden border border-[#C8D0E8] shadow-sm hover:shadow-lg hover:border-[#1B2A5E] transition-all cursor-pointer group">
                <div className="h-20 flex items-end px-5 pb-3" style={{ background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)` }}>
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="bg-slate-100 h-1">
                  <div className="h-full bg-gradient-to-r from-[#1B2A5E] to-[#C9A84C]" style={{ width: `${pct}%` }} />
                </div>
                <div className="p-4 bg-white">
                  <h3 className="font-bold text-[#1B2A5E] mb-1 line-clamp-2">{ep.programme_name}</h3>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Badge className="bg-[#EEF0F8] text-[#1B2A5E] border border-[#C8D0E8] text-[10px]">
                      {ep.cohort || `${ep.admission_window} Cohort`}
                    </Badge>
                    <Badge className={ep.status === 'active' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>
                      {ep.status || 'active'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{completedCount}/{progLessons.length} lessons · {pct}%</span>
                    <span className="text-[#1B2A5E] font-medium group-hover:underline">Open →</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── No enrolments: show browsable catalogue ── */
        <div className="space-y-6">
          <div className="text-center py-8 rounded-xl bg-[#EEF0F8] border border-[#C8D0E8]">
            <GraduationCap className="w-14 h-14 mx-auto mb-3 text-[#1B2A5E] opacity-40" />
            <p className="font-semibold text-[#1B2A5E]">You haven't enrolled in any programmes yet</p>
            <p className="text-sm text-slate-500 mt-1">Browse the available programmes below and enrol to get started.</p>
          </div>

          {availableProgrammes.length > 0 && (
            <>
              <h2 className="text-lg font-bold text-[#1B2A5E]">Available Programmes</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {availableProgrammes.map((p, i) => {
                  const colors = PROG_COLORS[i % PROG_COLORS.length];
                  return (
                    <div key={p.programme_id} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow bg-white">
                      <div className="h-20 flex items-end px-5 pb-3" style={{ background: `linear-gradient(135deg, ${colors.from} 0%, ${colors.to} 100%)` }}>
                        <Badge className="bg-white/20 text-white border-white/30 text-[10px]">{p.code}</Badge>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#1B2A5E] mb-0.5 line-clamp-1">{p.name}</h3>
                        {p.department && <p className="text-xs text-slate-400 mb-2">{p.department}</p>}
                        {p.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{p.description}</p>}
                        <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-4">
                          {p.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration}</span>}
                          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {p.course_count || 0} courses</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-[#1B2A5E]">GHS {Number(p.price || 0).toLocaleString()}</span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline"
                              className="rounded-lg h-8 text-xs border-[#1B2A5E] text-[#1B2A5E] hover:bg-[#EEF0F8]"
                              onClick={() => window.open(`/programmes/${p.programme_id}`, '_blank')}>
                              Details
                            </Button>
                            <Button size="sm"
                              className="rounded-lg h-8 text-xs bg-[#C9A84C] hover:bg-[#A07C28] text-white border-0"
                              onClick={() => openEnrolDialog(p)}>
                              Enrol <ChevronRight className="w-3 h-3 ml-0.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Enrolment dialog */}
      <Dialog open={enrolDialogOpen} onOpenChange={v => { if (!v) { setEnrolDialogOpen(false); setEnrolProg(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#1B2A5E]" /> Enrol in Programme
            </DialogTitle>
          </DialogHeader>
          {enrolProg && (
            <div className="space-y-5">
              <div className="p-4 rounded-xl bg-[#EEF0F8] border border-[#1B2A5E]/20">
                <h3 className="font-bold text-[#1B2A5E]">{enrolProg.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{enrolProg.department} · {enrolProg.duration}</p>
                {enrolProg.description && <p className="text-sm text-slate-600 mt-2">{enrolProg.description}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Select Admission Cohort</label>
                <Select value={enrolWindow} onValueChange={v => setEnrolWindow(v)}>
                  <SelectTrigger className="rounded-lg"><SelectValue placeholder="Choose a cohort…" /></SelectTrigger>
                  <SelectContent position="popper" className="z-[200]">
                    {ADMISSION_WINDOWS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Programme Fee</span>
                <span className="font-bold text-[#1B2A5E] text-lg">GHS {Number(enrolProg.price || 0).toLocaleString()}</span>
              </div>
              <PaystackCheckout programme={enrolProg} student={user} admissionWindow={enrolWindow}
                onSuccess={handleEnrolSuccess} onClose={() => setEnrolDialogOpen(false)} />
              <p className="text-[11px] text-slate-400 text-center">Secured payment · Bank card or Mobile Money accepted</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
